import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Container, Form, Row, Col } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import PageLoading from "components/PageLoading";
import NotificationToast from "components/NotificationToast/NotificationToast";
import BonusRangeTable from "../ComponentsExtractAnalysis/RangeTable/BonusRangeTable";
import BonusDocumentTable, {
  type BonusDocumentRow,
} from "../ComponentsExtractAnalysis/DocumentTable/BonusDocumentTable";
import GenericSmallTable from "../ComponentsExtractAnalysis/GenericTable/GenericSmallTable";
import DeducaoDocumentTable from "../ExtractModel/ComponentsExtractModel/DeducaoTable/DeducaoTable";
import { hasDeducaoAtrasoPreenchida } from "../V2/mapExtratoSolicitacaoApi";
import MockModeBanner from "./MockModeBanner";
import type { DadosDeducao } from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import { PATHS } from "../../../../routes/paths";
import { formatDateBR } from "../../../../utils/date";
import {
  getSindicatoById,
  getSindicatoByLoggedUser,
} from "../../../../services/sindicatoService";
import { getQuestionarioRespostaById } from "../../../../services";
import { getHistoricoAnalise } from "../../../../services/analiseService";
import {
  buildPreviewExtrato,
  getDeducoesExtrato,
} from "../../../../services/ExtractService";
import "../ExtractAnalysis/ExtractAnalysis.css";
import "./ExtractSindicatoDetalhe.css";

const toBRL = (value: number | null | undefined) =>
  Number(value ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const formatCNPJ = (cnpj: string | undefined) => {
  const onlyDigits = String(cnpj || "").replace(/\D/g, "");
  if (onlyDigits.length !== 14) return cnpj || "";
  return onlyDigits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

const CRITERIO_MAP: Record<string, number> = {
  "plano-de-negocio-sindical": 1,
  "renovacao-da-lideranca": 2,
  "balanco-patrimonial": 3,
  "relatorio-de-atividades": 4,
  "eventos-promovidos-pelo-sindicato": 5,
  "utilizacao-total-do-sistema-hi-gestor": 6,
  "evolucao-receita-operacional": 7,
  "80-de-retencao-de-associados": 8,
  "evolucao-do-associativismo": 9,
  "acao-internacional": 10,
  "defesa-de-interesse-implementada-no-executivo-e-legislativo": 11,
  "indicacao-de-membros-para-o-programa-fiemg-jovem": 12,
};

type DadosSindicato = {
  id?: string | number;
  nome?: string;
  cnpj?: string;
  sigla?: string;
  receitaSuperiorDozeSalarios?: boolean;
};

type PreviewExtrato = {
  valorMinimo?: number;
  valorBonus?: number;
  valorBonusExtra?: number;
  valorTotalApoioBruto?: number;
  receitaOperacional?: number;
  faixaReceitaBonus?: {
    titulo?: string;
    receitaMin?: number;
    receitaMax?: number;
    valorMinimo?: number;
    valorBonusPotencial?: number;
    valorMaximoPotencial?: number;
  };
  criterioBonus?: BonusDocumentRow[];
  criterioBonusExtra?: BonusDocumentRow[];
};

const ExtractSindicatoDetalheV1 = () => {
  const { solicitacaoAvaliacaoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state ?? {}) as {
    selectedYear?: number;
    solicitacao?: string;
    idSindicato?: string | number | null;
    situacao?: string;
  };

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dadosSindicato, setDadosSindicato] = useState<DadosSindicato>({
    nome: "",
    cnpj: "",
    sigla: "",
  });
  const [previewData, setPreviewData] = useState<PreviewExtrato | null>(null);
  const [deducaoData, setDeducaoData] = useState<DadosDeducao | null>(null);
  const [toastData, setToastData] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const idSindicatoState = routeState.idSindicato;

  useEffect(() => {
    const fetchDetalhe = async () => {
      if (!solicitacaoAvaliacaoId) {
        setErro("Solicitação não informada.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const sindicatoResponse = idSindicatoState
          ? await getSindicatoById(idSindicatoState)
          : await getSindicatoByLoggedUser();

        const sindicato = sindicatoResponse?.data ?? {};
        const idSindicato = idSindicatoState ?? sindicato?.id;
        if (!idSindicato) {
          throw new Error("Sindicato nao informado para carregar o extrato.");
        }

        const [respostaService, historicoA, deducoes] = await Promise.all([
          getQuestionarioRespostaById(solicitacaoAvaliacaoId),
          getHistoricoAnalise(solicitacaoAvaliacaoId),
          getDeducoesExtrato(idSindicato),
        ]);

        setDadosSindicato(sindicato);

        const rawJson = respostaService?.data?.json;
        const parsedJson =
          typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
        const temas = parsedJson?.docsObrigatorios?.[0]?.tema || [];

        const temaAdimplencia = temas.find(
          (t: { titulo?: string }) =>
            String(t.titulo || "").includes("ADIMPL") &&
            String(t.titulo || "").includes("FINANCEIRA"),
        );

        const criterioQuantas = temaAdimplencia?.criterio?.find(
          (c: { titulo?: string }) =>
            (c.titulo || "").includes("Quantas empresas"),
        );

        const respostaQuantas = criterioQuantas?.campo?.find(
          (c: { idCampo?: string; resposta?: unknown }) =>
            c.idCampo === "quantas-empresa",
        )?.resposta;

        const qtd = Number(respostaQuantas) || 0;
        const historicoArray =
          historicoA?.historicoAnaliseAdequacaoCampoQuestionarioJson || [];

        const criteriosBonusQuestionario = Array.from(
          historicoArray
            .reduce(
              (
                map: Map<number, { idCriterioBonus: number; conforme: boolean }>,
                item: { idCampoJson?: string; conforme?: boolean | null },
              ) => {
                if (!item.idCampoJson) return map;

                const idCriterioBonus = CRITERIO_MAP[item.idCampoJson];
                if (!idCriterioBonus) return map;

                const conforme =
                  item.conforme == null ? false : !!item.conforme;
                map.set(idCriterioBonus, { idCriterioBonus, conforme });
                return map;
              },
              new Map<number, { idCriterioBonus: number; conforme: boolean }>(),
            )
            .values(),
        );

        const preview = await buildPreviewExtrato({
          idQuestionario: Number(solicitacaoAvaliacaoId),
          quantidadeAdimplentes: qtd,
          receitaSuperiorDozeSalarios:
            sindicato?.receitaSuperiorDozeSalarios ?? false,
          criteriosBonusQuestionario,
        });

        setPreviewData(preview);
        setDeducaoData(deducoes);
      } catch (error) {
        console.error("Erro ao carregar extrato do sindicato:", error);
        setErro("Não foi possível carregar o extrato.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetalhe();
  }, [idSindicatoState, solicitacaoAvaliacaoId]);

  const atrasoDocRows = useMemo(() => {
    const atraso = deducaoData?.deducaoAtraso;
    if (!hasDeducaoAtrasoPreenchida(atraso) || atraso.deduzir === false) {
      return [];
    }
    return [
      {
        dataEnvio: formatDateBR(atraso.dataEnvio),
        dataLimite: formatDateBR(atraso.dataLimite ?? atraso.dataVencimento),
        diasAtraso: atraso.diasAtraso ?? 0,
        percentual: atraso.percentualDeducao ?? 0,
        valorApoio: toBRL(atraso.valorTotalApoio),
        valorDeducao: toBRL(atraso.valorDeducao),
      },
    ];
  }, [deducaoData]);

  const totaisDeducao = useMemo(() => {
    const atraso = deducaoData?.deducaoAtraso;
    const totalAtraso =
      hasDeducaoAtrasoPreenchida(atraso) && atraso.deduzir !== false
        ? Number(atraso.valorDeducao || 0)
        : 0;

    const totalTitulos = (deducaoData?.deducaoTitulos || [])
      .filter((item) => item.deduzir !== false)
      .reduce((acc, item) => acc + Number(item.valor || 0), 0);

    const totalOutras = (deducaoData?.deducaoOutros || [])
      .filter((item) => item.deduzir !== false)
      .reduce((acc, item) => acc + Number(item.valor || 0), 0);

    const totalGeral = totalAtraso + totalTitulos + totalOutras;
    const valorLiquido =
      Number(previewData?.valorTotalApoioBruto || 0) - totalGeral;

    return { totalAtraso, totalTitulos, totalOutras, totalGeral, valorLiquido };
  }, [deducaoData, previewData]);

  const dadosResumoApoio = [
    { label: "VALOR MÍNIMO", valor: toBRL(previewData?.valorMinimo) },
    { label: "BÔNUS ALCANÇADO", valor: toBRL(previewData?.valorBonus) },
    {
      label: "BÔNUS EXTRA ALCANÇADO",
      valor: toBRL(previewData?.valorBonusExtra),
    },
    {
      label: "VALOR TOTAL DO APOIO",
      valor: toBRL(previewData?.valorTotalApoioBruto),
    },
  ];

  const titleBonus: [string, string, string, string] = [
    "DOCUMENTO BÔNUS",
    "CUMPRIU?",
    "PERCENTUAL",
    "VALOR",
  ];
  const titleBonusExtra: [string, string, string, string] = [
    "DOCUMENTO BÔNUS EXTRA",
    "CUMPRIU?",
    "PERCENTUAL",
    "VALOR",
  ];

  if (loading) return <PageLoading />;

  if (erro) {
    return (
      <Container className="analysis-container py-4">
        <p className="text-danger">{erro}</p>
      </Container>
    );
  }

  return (
    <Container className="analysis-container py-4 extract-sindicato-detalhe">
      <MockModeBanner />

      <div
        className="merit-voltar-seta"
        style={{
          position: "absolute",
          top: "20px",
          right: "30px",
          zIndex: 10,
        }}
        onClick={() => navigate(PATHS.EXTRACT_SINDICATO)}
        title="Voltar para a lista"
      >
        <FaArrowLeft />
      </div>

      <div className="extract-analysis-header mb-3">
        <h1 className="extract-title mb-0">EXTRATO</h1>
      </div>

      <Form>
        <Form.Group className="mb-3">
          <Form.Label className="extract-label">NOME DO SINDICATO</Form.Label>
          <Form.Control type="text" value={dadosSindicato.nome} disabled />
        </Form.Group>

        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="extract-label">CNPJ DO SINDICATO</Form.Label>
              <Form.Control
                type="text"
                value={formatCNPJ(dadosSindicato.cnpj)}
                disabled
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="extract-label">SIGLA</Form.Label>
              <Form.Control type="text" value={dadosSindicato.sigla} disabled />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <Row className="valor-receber">
        <Col md={6}>
          <h3 className="receita-title">VALOR A RECEBER</h3>
          <Form.Group>
            <Form.Label className="extract-label">RECEITA OPERACIONAL</Form.Label>
            <Form.Control
              type="text"
              value={toBRL(previewData?.receitaOperacional)}
              disabled
            />
          </Form.Group>
        </Col>
      </Row>

      <BonusRangeTable
        tituloFaixa={previewData?.faixaReceitaBonus?.titulo}
        receitaMin={previewData?.faixaReceitaBonus?.receitaMin}
        receitaMax={previewData?.faixaReceitaBonus?.receitaMax}
        valorMinimo={previewData?.faixaReceitaBonus?.valorMinimo}
        valorBonusPotencial={previewData?.faixaReceitaBonus?.valorBonusPotencial}
        valorMaximoPotencial={
          previewData?.faixaReceitaBonus?.valorMaximoPotencial
        }
      />

      <div className="bonus-tables-wrapper">
        <BonusDocumentTable
          data={previewData?.criterioBonus || []}
          title={titleBonus}
        />
        <BonusDocumentTable
          data={previewData?.criterioBonusExtra || []}
          title={titleBonusExtra}
        />
      </div>

      <GenericSmallTable dados={dadosResumoApoio} corValor="verde" />

      <DeducaoDocumentTable
        atrasoDocs={atrasoDocRows}
        titulosEmAberto={(deducaoData?.deducaoTitulos || [])
          .filter((t) => t.deduzir !== false)
          .map((t) => ({
            filial: t.filial,
            titulo: t.titulo,
            natureza: t.natureza,
            emissao: formatDateBR(t.dataEmissao),
            vencimento: formatDateBR(t.dataVencimento),
            atrasoDias: t.atrasoDias ?? 0,
            valor: toBRL(t.valor),
            historico: t.historico,
          }))}
        outrasDeducoes={(deducaoData?.deducaoOutros || [])
          .filter((o) => o.deduzir !== false)
          .map((o) => ({
            descricao: o.descricao,
            valor: Number(o.valor) || 0,
          }))}
        totalAtraso={toBRL(totaisDeducao.totalAtraso)}
        totalTitulos={toBRL(totaisDeducao.totalTitulos)}
        totalOutras={toBRL(totaisDeducao.totalOutras)}
        totalGeral={toBRL(totaisDeducao.totalGeral)}
        liquidoApoio={toBRL(totaisDeducao.valorLiquido)}
      />

      <NotificationToast
        show={toastData.show}
        message={toastData.message}
        type={toastData.type}
        onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
      />
    </Container>
  );
};

export default ExtractSindicatoDetalheV1;
